import { useParams, Link } from 'react-router-dom';
import SEOHelmet from '@/components/base/SEOHelmet';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ComicCard from '@/components/base/ComicCard';
import { categories, getComicsByCategory } from '@/mocks/comics';

const CategoryPage = () => {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);
  const comics = slug ? getComicsByCategory(slug) : [];

  if (!category) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen pt-16 px-4 md:px-6">
          <div className="max-w-7xl mx-auto py-20 text-center">
            <h1 className="text-2xl font-bold text-foreground-950 font-heading">分类不存在</h1>
            <Link to="/" className="mt-4 inline-block text-primary-600 hover:text-primary-700 no-underline">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const catName = category.name;
  const pageTitle = `${catName}_韩国漫画推荐_韩漫在线免费阅读-韩漫屋`;
  const pageDesc = `韩漫屋${catName}分类提供高质量韩国漫画推荐，${category.description}。资源新颖画质优良，每日更新精选${catName}漫画。`;

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${catName} - 韩国漫画推荐`,
    description: pageDesc,
    url: `https://hanmanwu.com/category/${slug}`,
    mainEntity: {
      '@type': 'ItemList',
      itemListElement: comics.map((c, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        item: {
          '@type': 'CreativeWork',
          name: c.title,
          author: { '@type': 'Person', name: c.author },
          url: `https://hanmanwu.com/article/${c.id.replace('comic-', 'article-')}`,
        },
      })),
    },
  };

  return (
    <>
      <SEOHelmet
        title={pageTitle}
        description={pageDesc}
        keywords={`韩国漫画,${catName},韩漫推荐,韩漫在线免费阅读,韩漫屋`}
        canonicalUrl={`https://hanmanwu.com/category/${slug}`}
        schema={schema}
      />
      <Navbar />
      <main className="min-h-screen pt-16">
        <div className="px-4 md:px-6">
          <div className="max-w-7xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: catName },
              ]}
            />

            <div className="py-8 md:py-12">
              <div className="flex items-center gap-4 mb-2">
                <div className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-xl bg-primary-100 text-primary-600">
                  <i className={`${category.icon} text-lg md:text-xl`}></i>
                </div>
                <div>
                  <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading">
                    {catName}
                  </h1>
                  <p className="mt-1 text-sm text-foreground-600">
                    {category.description} · 共 {comics.length} 部作品
                  </p>
                </div>
              </div>
            </div>

            {comics.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-6 pb-14 md:pb-20" data-product-shop>
                {comics.map((comic) => (
                  <ComicCard key={comic.id} comic={comic} size="normal" />
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <p className="text-foreground-500">该分类暂无漫画，敬请期待</p>
              </div>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
};

export default CategoryPage;